import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import apiService from '../../service/ApiService';
import InitiationShell from './InitiationShell';
import './PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  facilitationSupport?: boolean;
};

const FacilitationSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [needed, setNeeded] = useState<boolean | null>(
    typeof state.facilitationSupport === 'boolean'
      ? state.facilitationSupport
      : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const goNext = async () => {
    if (needed === null) return;
    setSubmitError(null);

    if (!pocId) {
      setSubmitError('Missing PoC id. Complete earlier Initiation steps first.');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.saveFacilitationSupport(pocId, {
        facilitationSupportRequested: needed,
      });
      navigate('/pocs/new/review', {
        state: {
          ...state,
          facilitationSupport: needed,
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save facilitation support. Check Network tab (PoC must still be in Initiation draft).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InitiationShell activeStep="step5">
      <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Facilitation support
          </Headline>
          <Lead>
            Step 5 — Tell us if you need help running this Proof of Concept.
          </Lead>

          <p className="initiation__note">
            Facilitation support means a dedicated contact can help you align
            stakeholders, keep the PoC on track, and follow the platform
            process. Choose Yes if you want that help; choose No if you will
            run it yourself. Choosing Yes notifies GDI (backend email).
          </p>

          <div className="poc-type">
            <button
              type="button"
              className={
                'poc-type__option' +
                (needed === true ? ' poc-type__option--selected' : '')
              }
              disabled={isSubmitting}
              onClick={() => setNeeded(true)}
            >
              <strong>Yes</strong>
              <p>I need facilitation support for this PoC.</p>
            </button>

            <button
              type="button"
              className={
                'poc-type__option' +
                (needed === false ? ' poc-type__option--selected' : '')
              }
              disabled={isSubmitting}
              onClick={() => setNeeded(false)}
            >
              <strong>No</strong>
              <p>I will run this PoC without facilitation support.</p>
            </button>
          </div>

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/scorecard/result', { state })
              }
            >
              ← Back to scorecard result
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={needed === null || isSubmitting}
              onClick={goNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
    </InitiationShell>
  );
};

export default FacilitationSupportPage;
